import { AppTheme } from './theme.types';

/**
 * Default theme for AppointMe
 * This theme is based on the existing UI styles and serves as a base for customization
 */
const defaultTheme: AppTheme = {
  colors: {
    primary: '#3B82F6', // blue-500
    secondary: '#6B7280', // gray-500
    success: '#10B981', // green-500
    danger: '#EF4444', // red-500
    warning: '#F59E0B', // amber-500
    info: '#3B82F6', // blue-500
    light: '#F3F4F6', // gray-100
    dark: '#1F2937', // gray-800
    background: '#FFFFFF',
    text: {
      primary: '#1F2937', // gray-800
      secondary: '#4B5563', // gray-600
      muted: '#9CA3AF', // gray-400
    },
  },
  spacing: {
    xs: '0.25rem', // 4px
    sm: '0.5rem', // 8px
    md: '1rem', // 16px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
    xxl: '3rem', // 48px
  },
  fonts: {
    family: {
      base: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      heading: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    size: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      xxl: '1.5rem', // 24px
    },
    weight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  borders: {
    radius: {
      sm: '0.125rem', // 2px
      md: '0.375rem', // 6px
      lg: '0.5rem', // 8px
      full: '9999px',
    },
    width: {
      thin: '1px',
      normal: '2px',
      thick: '3px',
    },
  },
  calendar: {
    daySize: '2.5rem', // 40px
    selectedDayColor: '#FFFFFF',
    selectedDayBackground: '#3B82F6', // blue-500
    availableDayBackground: '#F3F4F6', // gray-100
    unavailableDayBackground: '#F3F4F6', // gray-100 with lighter opacity
    highlightedDayBorder: '2px solid #3B82F6', // blue-500
    weekdayHeaderColor: '#6B7280', // gray-500
    monthNavigationColor: '#1F2937', // gray-800,
    
    // Additional properties needed by the Calendar component
    disabledText: '#9CA3AF', // gray-400
    selectedBackground: '#3B82F6', // blue-500 - same as selectedDayBackground
    selectedText: '#FFFFFF', // white - same as selectedDayColor
    selectedHoverBackground: '#2563EB', // blue-600
    dayHoverBackground: '#F3F4F6', // gray-100
    highlightedBackground: '#EFF6FF', // blue-50
    highlightedText: '#1D4ED8', // blue-700
    outsideMonthText: '#D1D5DB', // gray-300
    navButtonHover: '#F3F4F6', // gray-100
    dayNameText: '#6B7280', // gray-500
  },
  inputs: {
    background: '#FFFFFF',
    border: '1px solid #D1D5DB', // gray-300
    focusBorder: '1px solid #3B82F6', // blue-500
    placeholderColor: '#9CA3AF', // gray-400
    textColor: '#1F2937', // gray-800
    errorColor: '#EF4444', // red-500
    disabledBackground: '#F3F4F6', // gray-100
    padding: '0.5rem 0.75rem', // 8px 12px
  },
  buttons: {
    primary: {
      background: '#3B82F6', // blue-500
      text: '#FFFFFF',
      border: 'none',
      hoverBackground: '#2563EB', // blue-600
      activeBackground: '#1D4ED8', // blue-700
      disabledBackground: '#BFDBFE', // blue-200
      disabledText: '#EFF6FF', // blue-50
    },
    secondary: {
      background: '#FFFFFF',
      text: '#1F2937', // gray-800
      border: '1px solid #D1D5DB', // gray-300
      hoverBackground: '#F9FAFB', // gray-50
      activeBackground: '#F3F4F6', // gray-100
      disabledBackground: '#F3F4F6', // gray-100
      disabledText: '#9CA3AF', // gray-400
    },
    danger: {
      background: '#EF4444', // red-500
      text: '#FFFFFF',
      border: 'none',
      hoverBackground: '#DC2626', // red-600
      activeBackground: '#B91C1C', // red-700
    },
  },
};

export default defaultTheme; 